import type { ComponentType } from "react";
import { CalendarIcon } from "lucide-react";
import { CalendarWidget } from "./components/widgets/CalendarWidget";
import { TodoWidget } from "./components/widgets/TodoWidget";
import { InboxWidget } from "./components/widgets/InboxWidget";
import { SettingsWidget } from "./components/widgets/SettingsWidget";
import { BlogCarouselWidget } from "./components/widgets/BlogCarouselWidget";
import { PortfolioWidget } from "./components/widgets/PortfolioWidget";
import { DeliveryManagerWidget } from "./components/widgets/DeliveryManagerWidget";
import { FeatureFlags, type FeatureFlagName } from "@/hooks/common/useFeatureFlag";

export type WidgetSize = 1 | 2;

export interface DragHandleProps {
  attributes: Record<string, unknown>;
  listeners: Record<string, unknown> | undefined;
}

export interface WidgetDefinition {
  id: string;
  label: string;
  component: ComponentType<{ dragHandleProps?: DragHandleProps }>;
  defaultSize: WidgetSize;
  defaultEnabled: boolean;
  defaultOrder: number;
  featureFlag?: FeatureFlagName;
  featureFallback?: ComponentType;
}

export interface WidgetLayoutItem {
  id: string;
  enabled: boolean;
  size: WidgetSize;
  order: number;
}

export type DashboardLayout = WidgetLayoutItem[];

export const CalendarWidgetFallback = () => (
  <div className="h-full rounded-2xl border border-dashed border-border bg-muted/50 px-4 py-6 text-center grid place-items-center">
    <div className="flex flex-col items-center gap-2">
      <CalendarIcon className="size-6 text-muted-foreground" aria-hidden="true" />
      <p className="text-muted-foreground text-sm">Calendar coming soon</p>
    </div>
  </div>
);

export const WIDGET_REGISTRY: WidgetDefinition[] = [
  {
    id: "portfolio",
    label: "Deal Pipeline",
    component: PortfolioWidget,
    defaultSize: 2,
    defaultEnabled: true,
    defaultOrder: 0,
  },
  {
    id: "calendar",
    label: "Calendar",
    component: CalendarWidget,
    defaultSize: 1,
    defaultEnabled: true,
    defaultOrder: 1,
    featureFlag: FeatureFlags.DEMO_CALENDAR,
    featureFallback: CalendarWidgetFallback,
  },
  {
    id: "todo",
    label: "To-Do",
    component: TodoWidget,
    defaultSize: 1,
    defaultEnabled: true,
    defaultOrder: 2,
  },
  {
    id: "delivery-manager",
    label: "Delivery Manager",
    component: DeliveryManagerWidget,
    defaultSize: 1,
    defaultEnabled: true,
    defaultOrder: 3,
  },
  {
    id: "inbox",
    label: "Inbox",
    component: InboxWidget,
    defaultSize: 1,
    defaultEnabled: true,
    defaultOrder: 4,
  },
  {
    id: "blog",
    label: "Blog",
    component: BlogCarouselWidget,
    defaultSize: 1,
    defaultEnabled: true,
    defaultOrder: 5,
  },
  {
    id: "settings",
    label: "Settings",
    component: SettingsWidget,
    defaultSize: 1,
    defaultEnabled: true,
    defaultOrder: 6,
  },
];

export interface BlogPost {
  image: string;
  title: string;
  description: string;
  date: string;
}

export const blogPosts: BlogPost[] = [
  {
    image:
      "https://images.unsplash.com/photo-1604594849809-dfedbc827105?w=600&h=450&fit=crop",
    title: "From Napkin Deal to Signed Contract in Minutes",
    description:
      "No more blank-page paralysis. Choose a template, answer a few questions, and generate a professional agreement in minutes.",
    date: "Mar 5, 2026",
  },
  {
    image:
      "https://images.unsplash.com/photo-1621981386829-9b458a2cddde?w=600&h=450&fit=crop",
    title: "Stop Losing Money on Deals You Don't Understand",
    description:
      "Every year, creators sign contracts they don't fully understand - and it costs them. Hidden recoupment clauses...",
    date: "Mar 3, 2026",
  },
  {
    image:
      "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=450&fit=crop",
    title: "One Platform to Track Every Deal in Motion",
    description:
      "Contracts, participants, documents, and deadlines - all in one place. One source of truth for every deal in motion.",
    date: "Mar 1, 2026",
  },
];
